import React from 'react'
import { 
  FileText,
  ExternalLink,
  AlertCircle
} from 'lucide-react'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'

interface IRSForm {
  id: string
  name: string
  formNumber: string
  description: string
  irsUrl: string
}

const irsForms: IRSForm[] = [
  {
    id: '1',
    name: 'Form 8824 - Like-Kind Exchanges',
    formNumber: '8824',
    description: 'Report the exchange of like-kind property',
    irsUrl: 'https://www.irs.gov/forms-pubs/about-form-8824'
  },
  {
    id: '2',
    name: 'Form 4797 - Sales of Business Property',
    formNumber: '4797',
    description: 'Report the sale or exchange of business property',
    irsUrl: 'https://www.irs.gov/forms-pubs/about-form-4797'
  },
  {
    id: '3',
    name: 'Form 4562 - Depreciation and Amortization',
    formNumber: '4562',
    description: 'Report depreciation and amortization deductions',
    irsUrl: 'https://www.irs.gov/forms-pubs/about-form-4562'
  },
  {
    id: '4',
    name: 'Schedule D - Capital Gains and Losses',
    formNumber: 'Schedule D',
    description: 'Report capital gains and losses from property sales',
    irsUrl: 'https://www.irs.gov/forms-pubs/about-schedule-d-form-1040'
  },
  {
    id: '5',
    name: 'Form 6252 - Installment Sale Income',
    formNumber: '6252',
    description: 'Report income from installment sales',
    irsUrl: 'https://www.irs.gov/forms-pubs/about-form-6252'
  },
  {
    id: '6',
    name: 'Form 8582 - Passive Activity Loss Limitations',
    formNumber: '8582',
    description: 'Calculate passive activity loss limitations',
    irsUrl: 'https://www.irs.gov/forms-pubs/about-form-8582'
  }
]

export const FormsPage: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900 dark:text-slate-100 flex items-center gap-3">
          <FileText className="h-7 w-7 text-primary-600 dark:text-primary-400" />
          IRS Forms & Documents
        </h1>
        <p className="text-secondary-600 dark:text-slate-400 mt-1">
          Access official IRS forms and resources for your 1031 exchange
        </p>
      </div>

      {/* Legal Disclaimer */}
      <Card className="p-6 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
              Important Legal Disclaimer
            </h3>
            <p className="text-sm text-yellow-800 dark:text-yellow-300 leading-relaxed">
              The information provided on this page is for general informational purposes only and does not constitute tax, legal, or financial advice. 
              Open1031 does not provide tax advice, tax preparation services, or legal counsel. The forms and resources linked here are provided by the 
              Internal Revenue Service (IRS) and are subject to change. You should consult with a qualified tax professional, certified public accountant, 
              or tax attorney regarding your specific tax situation and the completion of any tax forms. Open1031 is not responsible for any errors or 
              omissions in the information provided or for any actions taken based on this information.
            </p>
          </div>
        </div>
      </Card>

      {/* Forms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {irsForms.map((form) => (
          <Card key={form.id} className="p-6 hover:shadow-lg transition-shadow dark:bg-slate-800 dark:border-slate-700">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start space-x-3 flex-1">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-slate-100 mb-1">
                    {form.name}
                  </h3>
                  <p className="text-sm text-secondary-600 dark:text-slate-400 mb-2">
                    Form {form.formNumber}
                  </p>
                  <p className="text-sm text-secondary-700 dark:text-slate-300">
                    {form.description}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button
                variant="outline"
                onClick={() => window.open(form.irsUrl, '_blank', 'noopener,noreferrer')}
                className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-600"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View on IRS.gov
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Additional Resources */}
      <Card className="p-6 dark:bg-slate-800 dark:border-slate-700">
        <h2 className="text-lg font-semibold text-secondary-900 dark:text-slate-100 mb-4">
          Additional Resources
        </h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
            <div>
              <h3 className="text-sm font-medium text-secondary-900 dark:text-slate-100">
                IRS Publication 544 - Sales and Other Dispositions of Assets
              </h3>
              <p className="text-xs text-secondary-600 dark:text-slate-400 mt-1">
                Comprehensive guide to reporting property sales and exchanges
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('https://www.irs.gov/publications/p544', '_blank', 'noopener,noreferrer')}
              className="dark:bg-slate-600 dark:border-slate-500 dark:text-slate-200 dark:hover:bg-slate-500"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              View
            </Button>
          </div>
          <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
            <div>
              <h3 className="text-sm font-medium text-secondary-900 dark:text-slate-100">
                IRS Publication 523 - Selling Your Home
              </h3>
              <p className="text-xs text-secondary-600 dark:text-slate-400 mt-1">
                Information about tax implications of selling your home
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('https://www.irs.gov/publications/p523', '_blank', 'noopener,noreferrer')}
              className="dark:bg-slate-600 dark:border-slate-500 dark:text-slate-200 dark:hover:bg-slate-500"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              View
            </Button>
          </div>
          <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
            <div>
              <h3 className="text-sm font-medium text-secondary-900 dark:text-slate-100">
                IRS Topic No. 701 - Sale of Your Home
              </h3>
              <p className="text-xs text-secondary-600 dark:text-slate-400 mt-1">
                Overview of tax rules for home sales and exchanges
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('https://www.irs.gov/taxtopics/tc701', '_blank', 'noopener,noreferrer')}
              className="dark:bg-slate-600 dark:border-slate-500 dark:text-slate-200 dark:hover:bg-slate-500"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              View
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
